import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { useMemo, useState } from "react";
import { ConfirmFooter } from "./ConfirmFooter";
import styles from "./MoveUsersToTeamSheet.module.css";
import { SheetHeader } from "./SheetHeader";
import { StateMessage } from "./StateMessage";
import { TeamsList } from "./TeamsList";
import { TeamsSkeletonList } from "./TeamsSkeletonList";
import type { Team } from "./types";

interface MoveUsersToTeamSheetProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (team: Team) => void;
  teams: Team[];
  isLoading?: boolean;
  isError?: boolean;
}

export function MoveUsersToTeamSheet({
  visible,
  onClose,
  onConfirm,
  teams,
  isLoading = false,
  isError = false,
}: MoveUsersToTeamSheetProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(
    undefined
  );

  const filteredTeams = useMemo(
    () =>
      teams.filter((team) =>
        team.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      ),
    [teams, searchQuery]
  );

  const handleConfirm = () => {
    const selectedTeam = teams.find((team) => team.id === selectedTeamId);
    if (!selectedTeam) {
      return;
    }
    onConfirm(selectedTeam);
  };

  const bodyIsShown = !isLoading && !isError && teams.length > 0;
  const errorIsShown = !isLoading && isError;
  const emptyStateIsShown = !isLoading && !isError && teams.length === 0;
  const noResultsIsShown = bodyIsShown && filteredTeams.length === 0;
  const teamListIsShown = bodyIsShown && filteredTeams.length > 0;

  return (
    <FixaModal
      visible={visible}
      handleCloseBtn={onClose}
      data-testid="move-users-to-team-sheet"
    >
      <FixaSheets
        labelledById="move-users-to-team-header"
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            id="move-users-to-team-header"
            floating
            closeBtnClick={onClose}
          />
        }
        footer={
          <ConfirmFooter disabled={!selectedTeamId} onConfirm={handleConfirm} />
        }
      >
        <FixaModalBody className={styles["modal-body"]}>
          <SheetHeader
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            showSearch={bodyIsShown}
          />

          {isLoading && <TeamsSkeletonList />}

          {errorIsShown && <StateMessage type="error" />}

          {emptyStateIsShown && <StateMessage type="empty" />}

          {noResultsIsShown && <StateMessage type="noResults" />}

          {teamListIsShown && (
            <TeamsList
              teams={filteredTeams}
              selectedTeamId={selectedTeamId}
              onSelect={setSelectedTeamId}
            />
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
}
